/**
 * Surveys — M5 marketing module.
 *
 * Survey table (name / kind / questions / status / responses), a "New
 * survey" modal with dynamic question rows, and a results drawer with the
 * NPS gauge, per-question histograms, Publish/Close lifecycle and the raw
 * response table.
 *
 * Data: surveysService. MOCK_* fallbacks keep the page rendering without
 * a backend.
 */
import { useMemo, useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { surveysService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { useCustomerOptions } from '../lib/useDirectory';

const KIND_PILL   = { CSAT: 'info', NPS: 'accent', CUSTOM: '' };
const STATUS_PILL = { DRAFT: '', ACTIVE: 'success', CLOSED: 'warning' };
const QUESTION_TYPES = ['SCALE', 'TEXT', 'CHOICE'];

const fmtDate = (iso) => formatDateTime(iso);
const fmtNum  = (n)   => (n == null ? '—' : Number(n).toLocaleString('en-AU'));

/* ── Mock fallbacks ── */
const MOCK_SURVEYS = [
  {
    id: 'mock-s1', name: 'Post-Delivery NPS', kind: 'NPS', status: 'ACTIVE',
    createdAt: '2026-06-01T02:00:00Z', responseCount: 214,
    questions: [
      { id: 'q1', type: 'SCALE', prompt: 'How likely are you to recommend us? (0-10)' },
      { id: 'q2', type: 'TEXT',  prompt: 'What is the main reason for your score?' },
    ],
  },
  {
    id: 'mock-s2', name: 'Service Visit CSAT', kind: 'CSAT', status: 'ACTIVE',
    createdAt: '2026-05-14T02:00:00Z', responseCount: 96,
    questions: [
      { id: 'q1', type: 'SCALE',  prompt: 'How satisfied were you with your service visit? (1-5)' },
      { id: 'q2', type: 'CHOICE', prompt: 'What went best?', options: ['Speed', 'Price', 'Communication', 'Quality'] },
    ],
  },
  {
    id: 'mock-s3', name: 'Test Drive Feedback', kind: 'CUSTOM', status: 'DRAFT',
    createdAt: '2026-07-02T02:00:00Z', responseCount: 0,
    questions: [
      { id: 'q1', type: 'CHOICE', prompt: 'Which model did you drive?', options: ['SUV', 'Hatch', 'Ute'] },
    ],
  },
];
const MOCK_RESULTS = {
  responseCount: 214, nps: 42, promoters: 128, passives: 48, detractors: 38,
  questions: [
    {
      questionId: 'q1', type: 'SCALE',
      prompt: 'How likely are you to recommend us? (0-10)', answered: 214,
      histogram: { 6: 14, 7: 24, 8: 24, 9: 66, 10: 86 },
    },
    {
      questionId: 'q2', type: 'TEXT',
      prompt: 'What is the main reason for your score?', answered: 131, histogram: null,
    },
  ],
};
const MOCK_RESPONSES = {
  content: [
    { id: 'r1', customerId: 'cst-90210001', npsScore: 10, submittedAt: '2026-07-09T22:12:00Z',
      answers: { q1: 10, q2: 'Great handover experience' } },
    { id: 'r2', customerId: 'cst-90210002', npsScore: 7, submittedAt: '2026-07-08T06:44:00Z',
      answers: { q1: 7, q2: 'Delivery took longer than promised' } },
  ],
  page: 0, totalPages: 1, totalElements: 2, first: true, last: true,
};

export default function Surveys() {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  /* List + per-survey response counts (results call per row, best effort). */
  const { data: surveys, refetch } = useApi(
    async () => {
      const page = await surveysService.list({ size: 100 });
      const rows = Array.isArray(page) ? page : page?.content || [];
      return Promise.all(rows.map(async (s) => {
        try {
          const r = await surveysService.results(s.id);
          return { ...s, responseCount: r?.responseCount ?? 0 };
        } catch {
          return { ...s, responseCount: null };
        }
      }));
    },
    { fallback: MOCK_SURVEYS, deps: [] }
  );

  const publish = useMutation(surveysService.publish);
  const close   = useMutation(surveysService.close);

  const onLifecycle = async (mutation, past, survey) => {
    try {
      await mutation.mutate(survey.id);
      toast(`${past} "${survey.name}"`, { variant: 'success' });
      refetch();
    } catch (err) {
      toast(`${past} failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const rows = surveys || [];
  const selected = rows.find((s) => s.id === selectedId) || null;

  return (
    <>
      <TopBar
        crumbs={['Surveys']}
        right={
          <>
            <button className="btn small" onClick={refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setModalOpen(true)}>+ New survey</button>
          </>
        }
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Surveys</div>
            <div className="page-header-sub">CSAT, NPS and custom customer feedback</div>
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {['Name', 'Kind', 'Questions', 'Status', 'Responses', 'Created', 'Actions'].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                    No surveys yet — create one to start collecting feedback.
                  </td>
                </tr>
              )}
              {rows.map((s) => (
                <tr key={s.id} onClick={() => setSelectedId(s.id)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td><span className={`pill ${KIND_PILL[s.kind] || ''} filled`}>{s.kind}</span></td>
                  <td className="mono text-xs">{(s.questions || []).length}</td>
                  <td><span className={`pill ${STATUS_PILL[s.status] || ''} filled`}>{s.status}</span></td>
                  <td className="mono text-xs">{fmtNum(s.responseCount)}</td>
                  <td className="mono text-xs">{fmtDate(s.createdAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {s.status === 'DRAFT' && (
                        <button className="btn small primary" disabled={publish.mutating}
                                onClick={() => onLifecycle(publish, 'Published', s)}>
                          Publish
                        </button>
                      )}
                      {s.status === 'ACTIVE' && (
                        <button className="btn small" disabled={close.mutating}
                                onClick={() => onLifecycle(close, 'Closed', s)}>
                          Close
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {selected && (
          <SurveyDrawer
            survey={selected}
            onClose={() => setSelectedId(null)}
            onLifecycle={onLifecycle}
            publish={publish}
            closeMutation={close}
          />
        )}

        {modalOpen && (
          <NewSurveyModal
            onClose={() => setModalOpen(false)}
            onCreated={() => { setModalOpen(false); refetch(); }}
          />
        )}
      </div>
    </>
  );
}

/* ── Horizontal histogram bar list (divs, accent-colored) ── */
function Histogram({ histogram }) {
  const entries = Object.entries(histogram || {})
    .sort((a, b) => (isNaN(a[0]) || isNaN(b[0]) ? a[0].localeCompare(b[0]) : Number(a[0]) - Number(b[0])));
  if (entries.length === 0) {
    return <div className="text-muted text-xs">No structured answers yet.</div>;
  }
  const max = Math.max(...entries.map(([, v]) => Number(v) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {entries.map(([label, count]) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="mono text-xs text-muted"
                style={{ width: 90, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={label}>
            {label}
          </span>
          <div style={{ flex: 1, background: 'var(--bg-glass)', borderRadius: 4, height: 12 }}>
            <div style={{
              width: `${(100 * (Number(count) || 0)) / max}%`,
              height: '100%', borderRadius: 4,
              background: 'var(--accent-primary)',
            }} />
          </div>
          <span className="mono text-xs" style={{ width: 40, textAlign: 'right' }}>{fmtNum(count)}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Results drawer: NPS gauge, histograms, responses ── */
function SurveyDrawer({ survey, onClose, onLifecycle, publish, closeMutation }) {
  const [respPage, setRespPage] = useState(0);
  const [showResponses, setShowResponses] = useState(false);
  const { options: customerOptions } = useCustomerOptions();
  const customerName = useMemo(() => {
    const m = new Map(customerOptions.map(o => [o.id, o.primary]));
    return (id, fb) => fb || m.get(id) || (id ? `#${String(id).slice(0, 8)}` : '—');
  }, [customerOptions]);

  const { data: results } = useApi(
    () => surveysService.results(survey.id),
    { fallback: MOCK_RESULTS, deps: [survey.id] }
  );
  const { data: responses } = useApi(
    () => (showResponses
      ? surveysService.responses(survey.id, { page: respPage, size: 10 })
      : Promise.resolve(MOCK_RESPONSES)),
    { fallback: MOCK_RESPONSES, deps: [survey.id, respPage, showResponses] }
  );
  const respRows = responses?.content || [];

  const npsSplit = [
    ['Promoters',  results?.promoters,  'var(--success)'],
    ['Passives',   results?.passives,   'var(--warning)'],
    ['Detractors', results?.detractors, 'var(--danger)'],
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Survey ${survey.name}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(600px, 96vw)',
          background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
          overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div className="flex-between">
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{survey.name}</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {survey.kind} · {(survey.questions || []).length} questions · created {fmtDate(survey.createdAt)}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={`pill ${STATUS_PILL[survey.status] || ''} filled`}>{survey.status}</span>
          {survey.status === 'DRAFT' && (
            <button className="btn small primary" disabled={publish.mutating}
                    onClick={() => onLifecycle(publish, 'Published', survey)}>
              Publish
            </button>
          )}
          {survey.status === 'ACTIVE' && (
            <button className="btn small" disabled={closeMutation.mutating}
                    onClick={() => onLifecycle(closeMutation, 'Closed', survey)}>
              Close
            </button>
          )}
        </div>

        {/* Results summary */}
        <div className="card">
          <div className="flex-between">
            <span className="card-label" style={{ margin: 0 }}>RESULTS</span>
            <span className="mono text-xs text-muted">{fmtNum(results?.responseCount)} responses</span>
          </div>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <div className="mono text-xs text-muted">NPS</div>
              <div className="metric-value" style={{
                fontSize: 34,
                color: results?.nps == null ? 'var(--text-tertiary)'
                  : results.nps >= 30 ? 'var(--success)'
                  : results.nps >= 0 ? 'var(--warning)' : 'var(--danger)',
              }}>
                {results?.nps == null ? '—' : results.nps}
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {npsSplit.map(([label, value, color]) => (
                <div key={label} className="flex-between">
                  <span className="mono text-xs" style={{ color }}>{label}</span>
                  <span className="mono text-xs" style={{ color, fontWeight: 600 }}>{fmtNum(value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Per-question histograms */}
        {(results?.questions || []).map((q) => (
          <div key={q.questionId} className="card">
            <div className="flex-between" style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{q.prompt}</span>
              <span className="mono text-xs text-muted">{q.type} · {fmtNum(q.answered)} answered</span>
            </div>
            {q.type === 'TEXT'
              ? <div className="text-muted text-xs">Free-text answers — open the responses table below.</div>
              : <Histogram histogram={q.histogram} />}
          </div>
        ))}

        {/* Responses table */}
        <div className="card" style={{ padding: 0 }}>
          <div className="flex-between" style={{ padding: '12px 14px 6px' }}>
            <span className="card-label" style={{ margin: 0 }}>RESPONSES</span>
            <button className="btn small" onClick={() => setShowResponses((v) => !v)}>
              {showResponses ? 'Hide responses' : 'View responses'}
            </button>
          </div>
          {showResponses && (
            <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>{['Customer', 'NPS', 'Answers', 'Submitted'].map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {respRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                        No responses yet.
                      </td>
                    </tr>
                  )}
                  {respRows.map((r) => (
                    <tr key={r.id}>
                      <td>{customerName(r.customerId, r.customerName)}</td>
                      <td className="mono text-xs">{r.npsScore ?? '—'}</td>
                      <td className="text-xs" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {Object.entries(r.answers || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
                      </td>
                      <td className="mono text-xs">{fmtDate(r.submittedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <div className="flex-between" style={{ padding: '10px 14px' }}>
                <button className="btn small" disabled={responses?.first !== false}
                        onClick={() => setRespPage((p) => Math.max(0, p - 1))}>
                  ← Prev
                </button>
                <span className="mono text-xs text-muted">
                  page {(responses?.page ?? 0) + 1} / {Math.max(1, responses?.totalPages ?? 1)}
                </span>
                <button className="btn small" disabled={responses?.last !== false}
                        onClick={() => setRespPage((p) => p + 1)}>
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── "New survey" modal with dynamic question rows ── */
function NewSurveyModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('CSAT');
  const [questions, setQuestions] = useState([{ type: 'SCALE', prompt: '', options: '' }]);

  const create = useMutation(surveysService.create);

  const updateQuestion = (i, patch) =>
    setQuestions((rows) => rows.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const onSave = async () => {
    if (!name.trim()) {
      toast('Survey name is required', { variant: 'warning' });
      return;
    }
    const cleaned = questions.filter((q) => q.prompt.trim());
    if (cleaned.length === 0) {
      toast('Add at least one question with a prompt', { variant: 'warning' });
      return;
    }
    try {
      const body = {
        name: name.trim(),
        kind,
        questions: cleaned.map((q, i) => ({
          id: `q${i + 1}`,
          type: q.type,
          prompt: q.prompt.trim(),
          ...(q.type === 'CHOICE'
            ? { options: q.options.split(',').map((o) => o.trim()).filter(Boolean) }
            : {}),
        })),
      };
      const created = await create.mutate(body);
      toast(`Survey "${created?.name || name}" created as DRAFT`, { variant: 'success' });
      onCreated();
    } catch (err) {
      toast(`Create failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 'min(680px, 95vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">New survey</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="grid-2">
            <div className="form-field">
              <label>Name</label>
              <input value={name} placeholder="e.g. Post-delivery NPS"
                     onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Kind</label>
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="CSAT">CSAT</option>
                <option value="NPS">NPS</option>
                <option value="CUSTOM">CUSTOM</option>
              </select>
            </div>
          </div>

          <div className="card-label" style={{ marginTop: 4 }}>QUESTIONS</div>
          {questions.map((q, i) => (
            <div key={i} className="card" style={{ padding: 12 }}>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <span className="card-label" style={{ margin: 0 }}>QUESTION {i + 1}</span>
                {questions.length > 1 && (
                  <button className="btn small danger"
                          onClick={() => setQuestions((rows) => rows.filter((_, idx) => idx !== i))}>
                    Remove
                  </button>
                )}
              </div>
              <div className="grid-2" style={{ marginBottom: 8 }}>
                <div className="form-field">
                  <label>Type</label>
                  <select value={q.type} onChange={(e) => updateQuestion(i, { type: e.target.value })}>
                    {QUESTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Prompt</label>
                  <input value={q.prompt} placeholder="Question text"
                         onChange={(e) => updateQuestion(i, { prompt: e.target.value })} />
                </div>
              </div>
              {q.type === 'CHOICE' && (
                <div className="form-field">
                  <label>Options (comma-separated)</label>
                  <input value={q.options} placeholder="e.g. Speed, Price, Communication"
                         onChange={(e) => updateQuestion(i, { options: e.target.value })} />
                </div>
              )}
            </div>
          ))}
          <div>
            <button className="btn small"
                    onClick={() => setQuestions((rows) => [...rows, { type: 'SCALE', prompt: '', options: '' }])}>
              + Add question
            </button>
          </div>
        </div>

        <div className="modal-footer flex-between" style={{ padding: '14px 20px' }}>
          <button className="btn small" onClick={onClose}>Cancel</button>
          <button className="btn small primary" disabled={create.mutating} onClick={onSave}>
            {create.mutating ? 'Saving…' : 'Create survey'}
          </button>
        </div>
      </div>
    </div>
  );
}
